import axios, { AxiosInstance } from 'axios';
import { TemplatesResponse, RenderRequest, RenderResponse, InspectorModel, TemplateSummary, DynamicRenderRequest, DynamicRenderResponse, DataScenariosResponse, FeatureMetricResponse } from 'types/apis/cxEditor';
import { RunContextApp } from 'framework/RCApp';

// Lightweight API client for CX Visual Editor endpoints.
// Uses RCApp session to attach Authorization header.

class CxApiClient {
  private axiosClient: AxiosInstance;
  constructor(private rc: RunContextApp, private baseUrl: string) {
    this.axiosClient = axios.create();
    this.axiosClient.interceptors.request.use((config) => {
      const token = this.rc.session.getCMSAccessToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  }

  async getTemplates(): Promise<TemplatesResponse> {
    const resp = await this.axiosClient.get(`${this.baseUrl}/v1/cx/templates`);
    return resp.data as TemplatesResponse;
  }

  async getTemplateMeta(templateKey: string, elementKey?: string): Promise<{ templateKey: string; name: string; channel: string; elements: { elementKey: string; displayName: string }[]; scenarios: { label: string; value: string }[]; requestId?: string; }>{
    const params = elementKey ? { elementKey } : {};
    const resp = await this.axiosClient.get(`${this.baseUrl}/v1/cx/templates/${encodeURIComponent(templateKey)}`, { params });
    return resp.data;
  }

  async getScenarios(templateKey: string): Promise<{ templateKey: string; scenarios: { value: string; label: string; payload?: any }[]; requestId?: string; }>{
    // Use getTemplateMeta instead since /v1/cx/scenarios doesn't exist
    // Scenarios are returned as part of template metadata
    const resp = await this.axiosClient.get(`${this.baseUrl}/v1/cx/templates/${encodeURIComponent(templateKey)}`);
    return {
      templateKey: resp.data.templateKey || templateKey,
      scenarios: resp.data.scenarios || [],
      requestId: resp.data.requestId
    };
  }

  async getInspector(params: { templateKey: string; elementKey: string; hierarchy: string; locale: string; scenario: string; }): Promise<InspectorModel & { requestId?: string }>{
    const resp = await this.axiosClient.get(`${this.baseUrl}/v1/cx/inspector`, { params });
    return resp.data;
  }

  async getInspectorConfigs(params: {
    entityType: string;
    entityId: string;
    pilot_id: string;
    productType: string;
    notificationType: string;
    sectionName: string;
    locale: string;
    templateKey?: string | string[];
    /** For EMAIL channel when cluster chosen: comma-separated cluster references (e.g. "AMI" or "AMI,REF2"). */
    cluster_references?: string;
  }, options?: { signal?: AbortSignal }): Promise<{
    configs: {
      text: Array<{
        component: string;
        configDataType: string;
        configKey: string;
        configLevel: string;
        configRegex: string;
        configType: string;
        configValue: string;
        displayName: string;
        documentation: string;
        isPushedToUAT: boolean;
        possibleValues: any[];
        relatedConfigs: any[];
        section: string;
        source: string;
        attributes?: string[]; // For CDF support: array of tags like ["CDF_EL_GAS_WATER", "OTHER_TAG"]
        fuelSpecificValues?: Record<string, string>; // For CDF: { ELECTRIC: "value1", GAS: "value2" }
      }>;
      images: any[];
      colors: any[];
      metadata: any[];
    };
    stringResources: {
      text: Array<{
        stringResourceId: string;
        stringValue: string;
        displayName: string;
        documentation: string;
        locale: string;
        stringLevel: string;
        attributes?: string[]; // For CDF support: array of tags like ["CDF_EL_GAS_WATER", "OTHER_TAG"]
        fuelSpecificValues?: Record<string, string>; // For CDF: { ELECTRIC: "value1", GAS: "value2" }
        [key: string]: any; // Allow other fields
      }>;
      images: any[];
      colors: any[];
      metadata: any[];
    };
  }> {
    const url = `${this.baseUrl}/v1/cx/inspector`;
    console.log('[CxApiClient] getInspectorConfigs - Making API call to:', url);
    console.log('[CxApiClient] getInspectorConfigs - Params object:', params);
    
    // Ensure all params are strings and not undefined/null, but keep arrays as arrays
    const cleanParams: Record<string, string | string[]> = {};
    Object.keys(params).forEach(key => {
      const value = params[key as keyof typeof params];
      if (value !== undefined && value !== null) {
        // Keep arrays as arrays, convert others to strings
        if (Array.isArray(value)) {
          cleanParams[key] = value;
        } else {
          cleanParams[key] = String(value);
        }
      }
    });
    
    console.log('[CxApiClient] getInspectorConfigs - Cleaned params:', cleanParams);
    
    const resp = await this.axiosClient.get(url, {
      params: cleanParams,
      signal: options?.signal,
      paramsSerializer: (params) => {
        const searchParams = new URLSearchParams();
        Object.keys(params).forEach(key => {
          const value = params[key];
          if (Array.isArray(value)) {
            // Serialize array without brackets: templateKey=value (for each value)
            value.forEach(v => {
              searchParams.append(key, String(v));
            });
          } else {
            searchParams.append(key, String(value));
          }
        });
        return searchParams.toString();
      }
    });
    console.log('[CxApiClient] getInspectorConfigs - Response:', resp.data);
    return resp.data;
  }

  async renderHTML(request: RenderRequest): Promise<RenderResponse & { requestId?: string }>{
    const resp = await this.axiosClient.post(`${this.baseUrl}/v1/cx/render`, request);
    return resp.data;
  }

  async renderDynamic(
    request: DynamicRenderRequest,
    options?: { signal?: AbortSignal }
  ): Promise<DynamicRenderResponse & { requestId?: string }>{
    const resp = await this.axiosClient.post(`${this.baseUrl}/v1/cx/render`, request, { signal: options?.signal });
    return resp.data;
  }

  async saveChanges(payload: { templateKey: string; elementKey: string; variant: { hierarchy: string; locale: string; scenario: string }; changes: Partial<InspectorModel>; message?: string; }): Promise<{ versionId: string; auditId?: string; requestId?: string }>{
    const resp = await this.axiosClient.post(`${this.baseUrl}/v1/cx/save`, payload);
    return resp.data;
  }

  async save(payload: {
    project_id: string;
    configs: Array<{
      project_id: string;
      config_key: string;
      config_type: string;
      config_value: string;
      config_level: string;
      config_documentation: string;
      config_regex: string;
      config_data_type: string;
      display_name?: string;
      features?: string;
      editable?: boolean;
      is_required?: boolean;
      possible_values?: any[];
      related_configs?: any[];
      source?: string;
      product_type?: string;
      fuel_type?: string;
    }>;
    source_name?: string;
    enable_retry?: boolean;
    /** Locale from FE (e.g. "en_US"). */
    locale?: string;
    /** When hierarchy is cluster, the selected cluster reference (e.g. "AMI"). */
    cluster_reference?: string;
  }): Promise<{
    success: boolean;
    versionId?: string;
    auditId?: string;
    db_result?: {
      created_count: number;
      updated_count: number;
      results: Array<{
        config_key: string;
        project_id: string;
        action: 'created' | 'updated';
        level: string;
      }>;
    };
    push_result?: {
      ok: boolean;
      total: number;
      total_success: number;
      total_failed: number;
    };
    db_attempts?: number;
    push_attempts?: number;
    error?: string;
  }> {
    const resp = await this.axiosClient.post(`${this.baseUrl}/v1/cx/save`, payload);
    return resp.data;
  }

  async toggleOverride(payload: { templateKey: string; elementKey: string; field: { type: 'text'|'color'|'image'|'metadata'; id: string }; action: 'create'|'revert' }): Promise<{ ok: boolean; requestId?: string }>{
    const resp = await this.axiosClient.post(`${this.baseUrl}/v1/cx/override`, payload);
    return resp.data;
  }

  async signoff(payload: { templateKey: string; elementKey: string; variant: { hierarchy: string; locale: string; scenario: string }; signedOff: boolean }): Promise<{ ok: boolean; auditId?: string; requestId?: string }>{
    const resp = await this.axiosClient.post(`${this.baseUrl}/v1/cx/signoff`, payload);
    return resp.data;
  }

  async getPalette(): Promise<{ groups: Array<{ name: string; tokens: Array<{ id: string; label?: string; hex: string }> }> }>{
    const resp = await this.axiosClient.get(`${this.baseUrl}/v1/cx/palette`);
    return resp.data;
  }

  async getDataScenarios(
    pilot_id: string,
    fuel_type: string | null,
    locale: string,
    options?: { signal?: AbortSignal }
  ): Promise<DataScenariosResponse> {
    const params: Record<string, string> = { pilot_id, locale };
    if (fuel_type) {
      params.fuel_type = fuel_type;
    }
    const resp = await this.axiosClient.get(`${this.baseUrl}/v1/cx/data-scenarios`, {
      params,
      signal: options?.signal,
    });
    return resp.data;
  }

  async getDataScenarioDetails(
    pilotId: string,
    scenarioId: string,
    options?: { signal?: AbortSignal }
  ): Promise<{
    payloads: Array<{ documentId: string; payload: Record<string, unknown>; templateTypeName: string }>;
    scenarioId: string;
    templateName: string;
    productType: string;
    scenarioLocale: string;
  }> {
    const resp = await this.axiosClient.get(
      `${this.baseUrl}/v1/cx/pilot/${encodeURIComponent(pilotId)}/scenario/${encodeURIComponent(scenarioId)}`,
      { signal: options?.signal }
    );
    return resp.data?.data ?? resp.data;
  }

  async getDataScenariosByTemplate(templateName: string, pilotId: string, forceRefresh?: boolean, fuelType?: string): Promise<DataScenariosResponse> {
    const params: Record<string, string | boolean> = { templateName, pilot_id: pilotId };
    if (forceRefresh) {
      params.force_refresh = true;
    }
    if (fuelType) {
      params.fuel_type = fuelType;
    }
    const resp = await this.axiosClient.get(`${this.baseUrl}/v1/cx/data-scenarios`, { params });
    return resp.data;
  }

  async saveStrings(payload: {
    project_id: string;
    strings: Array<{
      project_id: string;
      string_key: string;
      string_value: string;
      locale: string;
      string_level: string;
      product_type?: string;
      fuel_type?: string;
    }>;
    source_name?: string;
    /** Locale from FE (e.g. "en_US"). */
    locale?: string;
    /** When hierarchy is cluster, the selected cluster reference (e.g. "AMI"). */
    cluster_reference?: string;
  }): Promise<{
    success: boolean;
    versionId?: string;
    auditId?: string;
    db_result?: {
      created_count: number;
      updated_count: number;
      results: Array<{
        string_key: string;
        project_id: string;
        action: 'created' | 'updated';
        level: string;
      }>;
    };
    push_result?: {
      ok: boolean;
      total: number;
      total_success: number;
      total_failed: number;
    };
    db_attempts?: number;
    push_attempts?: number;
    error?: string;
    warning?: string;
  }> {
    const resp = await this.axiosClient.post(`${this.baseUrl}/v1/cx/save-strings`, payload);
    return resp.data;
  }

  async getEditedConfigsForSection(params: {
    pilotId: string;
    templateKey: string;
    /** Template keys grouped by fuel type (e.g. { ELECTRIC: [...], GAS: [...], NONE: [...] }).
     *  Covers all fuel variants so the backend returns copy-history for every popup tab. */
    targetTemplateKeysByFuel?: Record<string, string[]>;
    section?: string;
    locale?: string;
    channel?: string;
    event?: string;
  }): Promise<{
    configs: Array<Record<string, any>>;
    strings: Array<Record<string, any>>;
    /** Per-target-template diff: configs/strings whose value differs from source.
     *  Used to show only the keys that can actually be copied to each target. */
    templateDiffs?: Record<string, {
      configs: Array<Record<string, any>>;
      strings: Array<Record<string, any>>;
    }>;
  }> {
    const body: Record<string, any> = {
      pilotId: params.pilotId,
      templateKey: params.templateKey,
      section: params.section || 'FULL_TEMPLATE',
    };
    if (params.locale) body.locale = params.locale;
    if (params.channel) body.channel = params.channel;
    if (params.event) body.event = params.event;
    if (params.targetTemplateKeysByFuel && Object.keys(params.targetTemplateKeysByFuel).length > 0) {
      body.targetTemplateKeysByFuel = params.targetTemplateKeysByFuel;
    }
    const resp = await this.axiosClient.post(`${this.baseUrl}/v1/cx/edited-configs-for-section`, body);
    return resp.data;
  }

  async completeExportAudit(batchKey: string): Promise<void> {
    await this.axiosClient.patch(`${this.baseUrl}/v1/cx/section-export-import-audit/${encodeURIComponent(batchKey)}/complete`);
  }

  async getFeatureMetrics(pilotId: string, level: 'LEVEL_1' | 'LEVEL_2'): Promise<FeatureMetricResponse> {
    const resp = await this.axiosClient.get(`${this.baseUrl}/v1/dcx-clusters/feature-metric/pilot/${pilotId}`, {
      params: { 
        level,
        //clusterReference: 'DEFAULT',
        excludedResolutions: 'KILL'
      }
    });
    return resp.data;
  }

  async getBulkManage(pilotId: string): Promise<any> {
    const resp = await this.axiosClient.get(`${this.baseUrl}/v1/dcx-clusters/bulk-manage/pilot/${pilotId}`);
    return resp.data;
  }

  /**
   * Fetch full HER preview HTML by posting the herPreviewUrl to /v1/cx/her-preview.
   * Returns the raw HTML string that can be rendered in an iframe via srcdoc.
   */
  async fetchHerPreviewHtml(
    herPreviewUrl: string,
    projectId: string,
    options?: { signal?: AbortSignal }
  ): Promise<string> {
    const resp = await this.axiosClient.post(
      `${this.baseUrl}/v1/cx/her-preview`,
      { url: herPreviewUrl, projectId },
      { signal: options?.signal }
    );
    if (typeof resp.data === 'string') {
      return resp.data;
    }
    return resp.data?.html || '';
  }

  async proxyImage(url: string): Promise<string | null> {
    try {
      const resp = await this.axiosClient.get(`${this.baseUrl}/v1/cx/proxy-image`, {
        params: { url },
        responseType: 'blob',
      });
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(resp.data);
      });
    } catch {
      return null;
    }
  }
}

export function createCxApiClient(rc: RunContextApp, baseUrl: string) {
  return new CxApiClient(rc, baseUrl);
}


